import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getSmallImageUrl } from '../utils/imageLoader'
import './Eshop.scss'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import renderOptions from '../utils/textRenderOption'
import FadeInImage from '../components/FadeInImage'

import Lenis from 'lenis';


const getIntrinsicImageDimensions = (product) => {
    const imageDetails = product?.asset?.fields?.file?.details?.image

    if (!imageDetails?.width || !imageDetails?.height) {
        return null
    }

    return {
        width: imageDetails.width,
        height: imageDetails.height,
    }
}


const EshopCategory = ({ products = [], category, eshopInfo = null }) => {
    const navigate = useNavigate()
    const [imageDimensions, setImageDimensions] = useState({})

    const filteredProducts = products.filter((product) =>
        Array.isArray(product.categorie) && product.categorie.includes(category)
    )

    // Lenis smooth scrolling (match other pages)
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Load image dimensions for all products
    useEffect(() => {
        filteredProducts.forEach((product) => {
            if (getIntrinsicImageDimensions(product) || imageDimensions[product.id]) return;

            const imageUrl = getSmallImageUrl(product.asset) || product.src;
            if (!imageUrl) return;

            const img = new Image();
            img.onload = () => {
                setImageDimensions(prev => ({
                    ...prev,
                    [product.id]: {
                        width: img.naturalWidth,
                        height: img.naturalHeight
                    }
                }));
            };
            img.src = imageUrl;
        });
    }, [filteredProducts, imageDimensions]);

    return (
        <div className='eshop'>
            <div className='eshopInfo'>
                {eshopInfo
                    && documentToReactComponents(eshopInfo, renderOptions)
                }
            </div>

            <div className='gridContainer'>
                {filteredProducts.map((product) => {
                    const productDimensions = getIntrinsicImageDimensions(product) || imageDimensions[product.id]
                    const caption = (product.caption || '').trim()
                    const captionParts = caption ? caption.split(' — ').map(p => p.trim()).filter(Boolean) : []
                    let price = (product.prix || '').toString().trim()
                    if (!price) {
                        const euroPart = captionParts.find(p => p.includes('€'))
                        if (euroPart) price = euroPart
                        else if (captionParts.length) price = captionParts[captionParts.length - 1]
                        else {
                            const m = caption.match(/(\d+[.,]?\d*)\s*€|€\s*(\d+[.,]?\d*)/)
                            if (m) price = (m[1] || m[2]) + ' €'
                        }
                    }
                    return (
                        <div
                            key={product.id}
                            className='productCard'
                        >
                            <div
                                className='imageWrapper'
                                style={{
                                    aspectRatio: productDimensions
                                        ? `${productDimensions.width} / ${productDimensions.height}`
                                        : '3/4',
                                }}
                            >
                                <FadeInImage
                                    src={getSmallImageUrl(product.asset) || product.src}
                                    alt={product.titre || 'Artwork'}
                                    {...(productDimensions?.width && { width: productDimensions.width })}
                                    {...(productDimensions?.height && { height: productDimensions.height })}
                                    onClick={() => product.path && navigate(product.path)}
                                />
                            </div>
                            <div
                                className='productInfo'
                                onClick={() => product.path && navigate(product.path)}
                            >
                                <p className='titre'>{product.titre}</p>
                                <p className='taille'>
                                    {(product.caption || '').split(' — ').find(part => part.includes('cm')) || ''}

                                </p>

                                <p className='prix'>{price} <span className={`stockDot ${product.stock ? 'inStock' : 'outStock'}`} /></p>

                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default EshopCategory
